import { ApiTypes } from '@app/api';

export type FailedTranslation = ApiTypes.FailedTranslationDto;

/** Language code → why that language got no suggestion. */
export type FailedTranslationsByLanguage = Record<string, FailedTranslation>;

/** Field key → language code → why that language got no suggestion. */
export type FailedTranslationsByField = Record<string, FailedTranslationsByLanguage>;

export interface AiTranslationOutcome {
  /** Field key → language code → suggested text. */
  translations: Record<string, Record<string, string>>;
  failures: FailedTranslationsByField;
  /** Languages that received a suggestion for at least one field. */
  translatedCodes: string[];
  /** Languages that came back without a suggestion for at least one field. */
  failedCodes: string[];
  /** Fields that failed as a whole, which carry no per-language reason of their own. */
  failedFieldCount: number;
}

/**
 * Merges the AI translate responses — the dialog sends one request per field length limit — into
 * what to fill in and what to flag. A 206 carries the same body as a 200.
 */
export function readAiTranslations(
  responses: ApiTypes.TranslateResponseDto[]
): AiTranslationOutcome {
  const translations: Record<string, Record<string, string>> = {};
  const failures: FailedTranslationsByField = {};
  const translatedCodes = new Set<string>();
  const failedCodes = new Set<string>();
  let failedFieldCount = 0;

  for (const response of responses) {
    failedFieldCount += response.failedCount;
    for (const [fieldKey, result] of Object.entries(response.results)) {
      if (result.status === 'success') {
        for (const [langCode, text] of Object.entries(result.translations)) {
          translations[fieldKey] = { ...(translations[fieldKey] ?? {}), [langCode]: text };
          translatedCodes.add(langCode);
        }
      }
      for (const failure of result.failedTranslations ?? []) {
        failures[fieldKey] = { ...(failures[fieldKey] ?? {}), [failure.language]: failure };
        failedCodes.add(failure.language);
      }
    }
  }

  return {
    translations,
    failures,
    translatedCodes: [...translatedCodes],
    failedCodes: [...failedCodes],
    failedFieldCount,
  };
}

/**
 * A new suggestion run settles every field and language it asked for, so their earlier failures
 * go, while languages it did not ask for keep theirs.
 */
export function mergeFailedTranslations(
  current: FailedTranslationsByField,
  incoming: FailedTranslationsByField,
  fieldKeys: string[],
  languages: string[]
): FailedTranslationsByField {
  const next: FailedTranslationsByField = {};
  for (const [fieldKey, byLanguage] of Object.entries(current)) {
    const kept = fieldKeys.includes(fieldKey)
      ? Object.fromEntries(Object.entries(byLanguage).filter(([code]) => !languages.includes(code)))
      : byLanguage;
    if (Object.keys(kept).length > 0) {
      next[fieldKey] = kept;
    }
  }
  for (const [fieldKey, byLanguage] of Object.entries(incoming)) {
    next[fieldKey] = { ...(next[fieldKey] ?? {}), ...byLanguage };
  }
  return next;
}

/**
 * Whether the failure should stay flagged once the admin has typed `value` into the field. Any edit
 * settles it, except text that is still over the limit — the rejected suggestion pasted in to be
 * shortened, for one.
 */
export function failureOutlivesEdit(failure: FailedTranslation, value: string): boolean {
  return (
    failure.reason === 'tooLong' &&
    typeof failure.maxLength === 'number' &&
    value.length > failure.maxLength
  );
}

/** Applies `failureOutlivesEdit` to one language of a per-language failure map. */
export function settleFailureOnEdit(
  failures: FailedTranslationsByLanguage,
  langCode: string,
  value: string
): FailedTranslationsByLanguage {
  const failure = failures[langCode];
  if (!failure || failureOutlivesEdit(failure, value)) {
    return failures;
  }
  return Object.fromEntries(Object.entries(failures).filter(([code]) => code !== langCode));
}
